import { NgIf } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import { ConfigurationService } from '@sc/data-access-configuration';
import { lang } from '@sc/data-access-models';
import { TranslatorService } from '@sc/data-access-translation';
import { FlagComponent, SelectComponent } from '@sc/ui-components';
import { LanguageDisplay } from '../../types';
import { languageDisplayMapping } from '../../utils';

@Component({
  selector: 'sc-language-selector',
  encapsulation: ViewEncapsulation.None,
  styleUrls: ['./language-selector.component.scss'],
  imports: [NgIf, SelectComponent, FlagComponent],
  template: `
    <sc-select
      *ngIf="showSelector()"
      [value]="currentLanguageCode"
      [options]="supportedLangs"
      optionKey="code"
      panelClass="lang-options-panel"
      (valueChange)="onChangeLanguage($event)"
    >
      <ng-template #triggerTemplate let-item>
        <sc-flag [lang]="currentLanguageCode"></sc-flag>
        <span class="country-label">{{ currentLanguageDisplay() }}</span>
      </ng-template>

      <ng-template #optionTemplate let-item>
        <sc-flag [lang]="item.code"></sc-flag>
        <span class="country-label">{{ item.displayed }}</span>
      </ng-template>
    </sc-select>
  `,
})
export class LanguageSelectorComponent {
  @Input() set languageCode(language: lang) {
    if (language) {
      this.updateCurrentLanguageCode(language);
    }
  }
  // The code of the currently selected language
  currentLanguageCode: lang;
  // The list of supported languages for this configuration
  supportedLangs: LanguageDisplay[];

  @Output() updateLanguage = new EventEmitter<lang>();

  constructor(
    private readonly translatorService: TranslatorService,
    private readonly configService: ConfigurationService
  ) {
    // Take every supported language, keep only the ones in the current config and format them to have their code and their name
    // Then sort the languages in alphabetical order
    this.supportedLangs = Object.keys(languageDisplayMapping)
      .map((key: lang) => ({
        displayed: languageDisplayMapping[key],
        code: key,
      }))
      .filter((lang) =>
        this.configService.getConfig().supportedLang.includes(lang.code)
      )
      .sort((a, b) => a.displayed.localeCompare(b.displayed));
    this.updateCurrentLanguageCode();
  }

  updateCurrentLanguageCode(language?: lang) {
    this.currentLanguageCode =
      language ||
      this.supportedLangs.find(
        (lang) =>
          lang.code === this.translatorService.getCurrentLang().getValue()
      )?.code ||
      this.currentLanguageCode ||
      this.configService.getConfig().defaultLang;
    this.translatorService.use(this.currentLanguageCode);
    // Init the current language by taking the one used by the translator
  }

  showSelector() {
    // Only show this selector if there are more than one choice
    return this.supportedLangs.length > 1;
  }

  currentLanguageDisplay() {
    // Return the name of the currently selected language
    return (
      this.supportedLangs.find((lang) => lang.code === this.currentLanguageCode)
        ?.displayed || 'Français'
    );
  }

  onChangeLanguage(event: lang) {
    // Change the translation language when the user selects one
    this.updateLanguage.emit(event);
    this.updateCurrentLanguageCode(event);
  }
}
